import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { PayMode } from '../utils/salary/pay-mode-salary';

const STORAGE_KEY = 'payDisplayMode';

interface PayModePreferenceContextValue {
  mode: PayMode;
  /** Sets the mode unconditionally. Callers that need the Sinulle eligibility gate (sign-in +
   *  resolved municipality) should check that themselves first (see useSinulleEligibility) and
   *  prompt sign-in/profile-location instead of calling this with 'foryou' when ineligible. */
  setMode: (mode: PayMode) => void;
}

/**
 * SRP B2 pay mode ("Brutto | Kateen | Sinulle", see mockups Wow2.dc.html/B2Full.dc.html): whether
 * job-card/list-row/bottom-panel salaries show the raw gross figure (default, matches every
 * visitor's experience today), an estimated generic Finnish take-home figure, or a take-home
 * figure for the signed-in user's own municipality. Client-only (localStorage), not read during
 * SSR: the static/first-hydrated paint always shows gross, same as today; a returning visitor's
 * choice applies after mount. Sibling to SalaryDisplayPreferenceContext (currency mode) and
 * CompanyGroupingPreferenceContext (grouping mode), same persistence pattern.
 */
const PayModePreferenceContext = createContext<PayModePreferenceContextValue>({
  mode: 'gross',
  setMode: () => {},
});

export function PayModePreferenceProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [mode, setModeState] = useState<PayMode>('gross');

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (stored === 'gross' || stored === 'net' || stored === 'foryou') {
        setModeState(stored);
      }
    } catch {
      // localStorage unavailable (private browsing, etc.) -- stay on the default.
    }
  }, []);

  const setMode = useCallback((next: PayMode) => {
    setModeState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Best-effort persistence only.
    }
  }, []);

  return <PayModePreferenceContext.Provider value={{ mode, setMode }}>{children}</PayModePreferenceContext.Provider>;
}

export function usePayModePreference(): PayModePreferenceContextValue {
  return useContext(PayModePreferenceContext);
}
